import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import { ENDPOINTS } from "../../services/endpoints";
import { IMAGES } from "../../constants/image";
import forgotConfig from "../../config/forms/forgot.json";
import { validateValue } from "../../utils/validation";
import usePopup from "../../hooks/usePopup";
import { extractMessageResponse } from "../../utils/messageResponse";

interface FieldValidation {
  label?: string;
  required?: boolean;
  pattern?: string;
  minLength?: number;
  maxLength?: number;
}

interface FormField {
  name: string;
  type: string;
  label?: string;
  placeholder?: string;
  validation?: FieldValidation;
}

const getApiMessage = (value: any, fallback: string): string => {
  if (!value) return fallback;
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.filter(Boolean).join(" ") || fallback;
  if (typeof value === "object") {
    const message = value.error || value.message || value.errors;
    if (message) return getApiMessage(message, fallback);
    return (
      Object.values(value)
        .map((item) => getApiMessage(item, ""))
        .filter(Boolean)
        .join(" ") || fallback
    );
  }
  return String(value);
};

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const showPopup = usePopup();
  const [formData, setFormData] = useState<any>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fields = forgotConfig.fields as FormField[];
  const ICON_MAP: Record<string, string> = {
    email: IMAGES.ICONS.EMAIL,
  };

  const validateField = (name: string, value: string) => {
    const field = fields.find((f) => f.name === name);
    if (!field) return "";

    return (
      validateValue(value, {
        label: field.label,
        ...(field.validation || {}),
      }) || ""
    );
  };
  const handleCancel = () => {
    setFormData({});
    setErrors({});
  };
  const validateForm = () => {
    const nextErrors: Record<string, string> = {};
    fields.forEach((field) => {
      const error = validateField(field.name, formData[field.name] || "");
      if (error) nextErrors[field.name] = error;
    });
    return nextErrors;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    try {
      const response = await api.post(ENDPOINTS.AUTH.FORGOT_PASSWORD, {
        email: formData.email,
      });

      const messageResponse = extractMessageResponse(
        response?.data,
        "Please check your email for reset instructions.",
        200,
      );
      showPopup({
        status: messageResponse.status,
        message: messageResponse.message,
        messageType: messageResponse.messageType,
        messageCode: messageResponse.messageCode,
        onOk: () => navigate("/login", { replace: true }),
      });
    } catch (error: any) {
      const apiData = error?.response?.data;
      const messageResponse = extractMessageResponse(apiData, "Request failed", 500);
      const message = messageResponse.message;
      setErrors((prev) => ({
        ...prev,
        apiError: message,
      }));
      showPopup({
        status: messageResponse.status,
        message,
        messageType: messageResponse.messageType,
        messageCode: messageResponse.messageCode,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="forgot-wrapper">
      <div className="forgot-card">
        <h3 className="forgot-title">Reset Password</h3>

        <form className="forgot-form" onSubmit={handleSubmit}>
          {fields.map((field) => {
            const value = formData[field.name] || "";
            const error = errors[field.name];

            return (
              <div className="form-group" key={field.name}>
                <label className="form-label">{field.label}</label>

                <div className="input-group-custom">
                  <input
                    type={field.type}
                    name={field.name}
                    className={`form-control custom-input form-input  ${error ? "input-error" : ""}`}
                    placeholder={field.placeholder}
                    value={value}
                    onChange={handleChange}
                  />

                  {ICON_MAP[field.name] && (
                    <img
                      src={ICON_MAP[field.name]}
                      alt={field.name}
                      className="input-icon"
                    />
                  )}
                </div>

                {error && <small className="error-text">{error}</small>}
              </div>
            );
          })}

          {errors.apiError && (
            <small className="error-text">{errors.apiError}</small>
          )}
          <div className="d-flex gap-2 forgot-action-row">
            <button
              type="submit"
              className=" btn forgot-btn-primary w-100 forgot-action-btn "
              disabled={isSubmitting}
            >
              {isSubmitting ? "Processing..." : "Send Reset Link"}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary w-100 forgot-action-btn forgot-cancel-btn"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

    </div>
  );
};

export default ForgotPassword;
